/**
 * Página de Perfil Completo do Usuário — Admin
 * Visualização completa de todos os dados do usuário
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import operacionalService from '../../services/operacionalService';

/* ─── Palette ─────────────────────────────────────────────────────────── */
const ORANGE  = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE    = '#2563EB';
const GREEN   = '#10B981';
const RED     = '#EF4444';
const AMBER   = '#F59E0B';
const DARK    = '#0F172A';

/* ─── KPI Card ──────────────────────────────────────────────────────────── */
const KpiCard = ({ title, value, subtitle, icon: Icon, gradient }) => (
  <Card sx={{
    borderRadius: 2,
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    height: '100%',
  }}>
    <Box sx={{ height: 4, background: gradient }} />
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: DARK, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 1.5,
          background: gradient, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </Box>
  </Card>
);

/* ─── Tab Item ──────────────────────────────────────────────────────────── */
const TabItem = ({ label, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none',
      borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
      display: 'flex', alignItems: 'center', gap: 1,
      transition: 'all 0.2s',
      '&:hover': { bgcolor: '#F8FAFC' },
    }}
  >
    <Typography sx={{
      fontSize: '0.875rem', fontWeight: active ? 600 : 400,
      color: active ? DARK : '#64748B',
    }}>
      {label}
    </Typography>
    {count !== undefined && (
      <Box sx={{
        bgcolor: active ? `rgba(249,115,22,0.12)` : '#F1F5F9',
        color: active ? ORANGE : '#64748B',
        borderRadius: 10, px: 1, minWidth: 22, height: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.7rem', fontWeight: 700,
      }}>
        {count}
      </Box>
    )}
  </Box>
);

/* ─── Info Row ──────────────────────────────────────────────────────────── */
const InfoRow = ({ icon: Icon, label, value, iconColor = ORANGE }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
    borderBottom: '1px solid #F1F5F9', '&:last-child': { borderBottom: 'none' } }}>
    <Box sx={{
      width: 34, height: 34, borderRadius: 1, flexShrink: 0,
      bgcolor: `${iconColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon sx={{ fontSize: 17, color: iconColor }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontWeight: 500, lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: DARK, fontWeight: 500, mt: 0.2 }}>
        {value || 'Não informado'}
      </Typography>
    </Box>
  </Box>
);

/* ─── Empty State ───────────────────────────────────────────────────────── */
const EmptyState = ({ icon: Icon, message }) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Icon sx={{ fontSize: 52, color: '#CBD5E1', mb: 1 }} />
    <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem' }}>{message}</Typography>
  </Box>
);

/* ─── Table Head Cell ───────────────────────────────────────────────────── */
const TH = ({ children, align }) => (
  <TableCell align={align} sx={{
    fontWeight: 600, fontSize: '0.72rem', color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.04em',
    bgcolor: '#F8FAFC', py: 1.4,
    borderBottom: '1px solid #E2E8F0',
  }}>
    {children}
  </TableCell>
);

/* ─── Chip de Tipo ──────────────────────────────────────────────────────── */
const TipoChip = ({ tipo }) => {
  const map = {
    PF:      { label: 'Pessoa Física',    bg: `${ORANGE}18`,  color: ORANGE },
    PJ:      { label: 'Pessoa Jurídica',  bg: `${BLUE}18`,    color: BLUE   },
    HIBRIDO: { label: 'Híbrido',          bg: `${AMBER}18`,   color: AMBER  },
  };
  const cfg = map[tipo] || map.PF;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      bgcolor: cfg.bg, color: cfg.color,
      borderRadius: 1, px: 1.2, py: 0.3,
      fontSize: '0.72rem', fontWeight: 700,
    }}>
      {cfg.label}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Componente Principal
   ═══════════════════════════════════════════════════════════════════════════ */
const PerfilUsuario = () => {
  const { userId } = useParams();
  const navigate   = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [perfil, setPerfil]     = useState(null);
  const [tab, setTab]           = useState(0);

  /* upload */
  const [uploadOpen, setUploadOpen]   = useState(false);
  const [uploadData, setUploadData]   = useState({
    tipo: 'nota_fiscal', descricao: '', dataEmissao: '', valor: '', observacoes: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading]       = useState(false);

  useEffect(() => { carregarPerfil(); }, [userId]); // eslint-disable-line

  const carregarPerfil = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await operacionalService.getPerfilCompleto(userId);
      setPerfil(data);
    } catch (err) {
      console.error('Erro ao carregar perfil:', err);
      setError('Erro ao carregar perfil do usuário');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) =>
    `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fmtDate = (d) => new Date(d).toLocaleDateString('pt-BR');

  /* upload handlers */
  const handleUploadClose = () => {
    setUploadOpen(false);
    setUploadData({ tipo: 'nota_fiscal', descricao: '', dataEmissao: '', valor: '', observacoes: '' });
    setSelectedFile(null);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) { alert('Selecione um arquivo'); return; }
    try {
      setUploading(true);
      await operacionalService.uploadDocumento(userId, uploadData, selectedFile);
      handleUploadClose();
      carregarPerfil();
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      alert('Erro ao fazer upload do documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (id) => {
    try { await operacionalService.downloadDocumento(id); }
    catch (err) { console.error(err); alert('Erro ao fazer download'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await operacionalService.deleteDocumento(id);
      carregarPerfil();
    } catch (err) { console.error(err); alert('Erro ao excluir documento'); }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: ORANGE }} />
      </Box>
    );
  }

  /* ─── Error ─── */
  if (error || !perfil) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Perfil não encontrado'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/operacional')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  const { usuario, estatisticas, faturamentos, despesas, documentos } = perfil;

  const initials = (usuario.nome || '?')
    .split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

  /* ─── Render ─── */
  return (
    <Box>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${DARK} 0%, #1E3A5F 60%, #0C2340 100%)`,
        borderRadius: 3, mb: 3, px: { xs: 3, md: 4 }, py: 4, position: 'relative', overflow: 'hidden',
      }}>
        {/* decorative circles */}
        {[
          { size: 220, top: -80,  right: -60, op: 0.07 },
          { size: 140, top:  60,  right:  80, op: 0.05 },
          { size:  90, bottom: -20, left: 200, op: 0.06 },
        ].map((c, i) => (
          <Box key={i} sx={{
            position: 'absolute', width: c.size, height: c.size, borderRadius: '50%',
            border: `1px solid rgba(255,255,255,${c.op})`,
            top: c.top, right: c.right, bottom: c.bottom, left: c.left,
            pointerEvents: 'none',
          }} />
        ))}

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* back */}
          <Box
            onClick={() => navigate('/admin/operacional')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.8,
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', mb: 2.5,
              fontSize: '0.8rem', fontWeight: 500,
              '&:hover': { color: '#fff' }, transition: 'color 0.15s',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Voltar ao Operacional
          </Box>

          {/* identity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Avatar sx={{
              width: 64, height: 64, fontSize: '1.4rem', fontWeight: 700,
              background: `linear-gradient(135deg, ${BLUE} 0%, #1D4ED8 100%)`,
              border: '3px solid rgba(255,255,255,0.15)',
            }}>
              {initials}
            </Avatar>

            <Box>
              <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 }}>
                {usuario.nome}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', mt: 0.3 }}>
                {usuario.email}
              </Typography>
            </Box>

            {/* chips */}
            <Box sx={{ display: 'flex', gap: 1, ml: { md: 'auto' }, flexWrap: 'wrap', alignItems: 'center' }}>
              {usuario.tipoPessoa && (
                <Box sx={{
                  bgcolor: `${ORANGE}25`, border: `1px solid ${ORANGE}50`,
                  color: ORANGE_L, borderRadius: 1, px: 1.5, py: 0.5,
                  fontSize: '0.75rem', fontWeight: 600,
                }}>
                  {usuario.tipoPessoa === 'PF' ? 'Pessoa Física' : usuario.tipoPessoa === 'PJ' ? 'Pessoa Jurídica' : 'Híbrido'}
                </Box>
              )}
              <Box sx={{
                bgcolor: usuario.ativo ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                border: `1px solid ${usuario.ativo ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
                color: usuario.ativo ? '#6EE7B7' : '#FCA5A5',
                borderRadius: 1, px: 1.5, py: 0.5,
                fontSize: '0.75rem', fontWeight: 600,
              }}>
                {usuario.ativo ? '● Ativo' : '● Inativo'}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Faturamento Total"
            value={fmt(estatisticas.valorTotalFaturamento)}
            subtitle={`${estatisticas.totalFaturamentos} lançamentos`}
            icon={TrendingUpIcon}
            gradient={`linear-gradient(135deg, ${GREEN} 0%, #059669 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Despesas Totais"
            value={fmt(estatisticas.valorTotalDespesas)}
            subtitle={`${estatisticas.totalDespesas} lançamentos`}
            icon={TrendingDownIcon}
            gradient={`linear-gradient(135deg, ${RED} 0%, #DC2626 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Saldo"
            value={fmt(estatisticas.saldo)}
            subtitle={estatisticas.saldo >= 0 ? 'Positivo' : 'Negativo'}
            icon={AccountBalanceIcon}
            gradient={estatisticas.saldo >= 0
              ? `linear-gradient(135deg, ${BLUE} 0%, #1D4ED8 100%)`
              : `linear-gradient(135deg, ${RED} 0%, #DC2626 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Documentos"
            value={estatisticas.totalDocumentos}
            subtitle="Arquivos salvos"
            icon={DescriptionIcon}
            gradient={`linear-gradient(135deg, ${AMBER} 0%, #D97706 100%)`}
          />
        </Grid>
      </Grid>

      {/* ── Tabs + Content ───────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

        {/* tab bar */}
        <Box sx={{ borderBottom: '1px solid #E2E8F0', display: 'flex', px: 1, bgcolor: '#fff', overflowX: 'auto' }}>
          {[
            { label: 'Informações Gerais' },
            { label: 'Faturamentos',  count: faturamentos.length  },
            { label: 'Despesas',      count: despesas.length      },
            { label: 'Documentos',    count: documentos.length    },
          ].map((t, i) => (
            <TabItem key={i} label={t.label} count={t.count} active={tab === i} onClick={() => setTab(i)} />
          ))}
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>

          {/* ── Tab 0: Informações Gerais ─── */}
          {tab === 0 && (
            <Grid container spacing={3}>
              {/* Dados Pessoais */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                  <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK }}>
                      Dados Pessoais
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 1 }}>
                    <InfoRow icon={PersonIcon}  label="Nome"          value={usuario.nome} />
                    <InfoRow icon={EmailIcon}   label="E-mail"        value={usuario.email} />
                    <InfoRow icon={PhoneIcon}   label="Telefone"      value={usuario.telefone} />
                    <InfoRow icon={BusinessIcon} label="Tipo de Pessoa"
                      value={usuario.tipoPessoa === 'PF' ? 'Pessoa Física' : usuario.tipoPessoa === 'PJ' ? 'Pessoa Jurídica' : 'Híbrido'} />
                    <InfoRow icon={CalendarIcon} label="Cadastrado em" value={fmtDate(usuario.createdAt)} />
                  </Box>
                </Card>
              </Grid>

              {/* Dados da Clínica + Financeiro */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: 'none', mb: 2.5 }}>
                  <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK }}>
                      Informações da Clínica
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 1 }}>
                    <InfoRow icon={BusinessIcon} label="Nome da Clínica" value={usuario.nomeClinica}   iconColor={BLUE} />
                    <InfoRow icon={PersonIcon}   label="CRO"             value={usuario.cro}            iconColor={BLUE} />
                    <InfoRow icon={ReceiptLongIcon} label="CPF / CNPJ"  value={usuario.cpf || usuario.cnpj} iconColor={BLUE} />
                    <InfoRow icon={PersonIcon}   label="Profissão"       value={usuario.profissao || 'Dentista'} iconColor={BLUE} />
                  </Box>
                </Card>

                {/* Análise Financeira */}
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: 'none', bgcolor: '#F8FAFC' }}>
                  <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK }}>
                      Análise Financeira
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      { label: 'Faturamento',         value: fmt(estatisticas.valorTotalFaturamento), color: GREEN  },
                      { label: 'Despesas',             value: fmt(estatisticas.valorTotalDespesas),   color: RED    },
                      { label: 'Impostos Estimados',   value: fmt(estatisticas.impostosEstimados),    color: AMBER  },
                      { label: 'Lucro Líquido',        value: fmt(estatisticas.lucroLiquido),
                        color: estatisticas.lucroLiquido >= 0 ? GREEN : RED },
                    ].map((row, i, arr) => (
                      <Box key={i} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        pb: i < arr.length - 1 ? 1.5 : 0,
                        borderBottom: i < arr.length - 1 ? '1px dashed #E2E8F0' : 'none',
                      }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: row.color }}>
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* ── Tab 1: Faturamentos ─── */}
          {tab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: DARK }}>
                  Histórico de Faturamentos
                </Typography>
                <Box sx={{
                  bgcolor: `${GREEN}15`, color: GREEN,
                  borderRadius: 1, px: 1.5, py: 0.5, fontSize: '0.85rem', fontWeight: 700,
                }}>
                  Total: {fmt(estatisticas.valorTotalFaturamento)}
                </Box>
              </Box>

              {faturamentos.length === 0 ? (
                <EmptyState icon={TrendingUpIcon} message="Nenhum faturamento registrado" />
              ) : (
                <TableContainer>
                  <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F1F5F9' } }}>
                    <TableHead>
                      <TableRow>
                        <TH>Data</TH>
                        <TH>Paciente</TH>
                        <TH>Procedimento</TH>
                        <TH align="right">Valor</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {faturamentos.map((fat) => (
                        <TableRow key={fat.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                          <TableCell sx={{ fontSize: '0.85rem', color: '#64748B', py: 1.4 }}>
                            {fmtDate(fat.data)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: DARK, fontWeight: 500, py: 1.4 }}>
                            {fat.paciente || 'Não informado'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', color: '#64748B', py: 1.4 }}>
                            {fat.procedimento || 'Não informado'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.4 }}>
                            <Typography sx={{ fontWeight: 700, color: GREEN, fontSize: '0.875rem' }}>
                              {fmt(fat.valor)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ── Tab 2: Despesas ─── */}
          {tab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: DARK }}>
                  Histórico de Despesas
                </Typography>
                <Box sx={{
                  bgcolor: `${RED}15`, color: RED,
                  borderRadius: 1, px: 1.5, py: 0.5, fontSize: '0.85rem', fontWeight: 700,
                }}>
                  Total: {fmt(estatisticas.valorTotalDespesas)}
                </Box>
              </Box>

              {despesas.length === 0 ? (
                <EmptyState icon={TrendingDownIcon} message="Nenhuma despesa registrada" />
              ) : (
                <TableContainer>
                  <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F1F5F9' } }}>
                    <TableHead>
                      <TableRow>
                        <TH>Data</TH>
                        <TH>Descrição</TH>
                        <TH>Categoria</TH>
                        <TH align="right">Valor</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {despesas.map((desp) => (
                        <TableRow key={desp.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                          <TableCell sx={{ fontSize: '0.85rem', color: '#64748B', py: 1.4 }}>
                            {fmtDate(desp.data)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: DARK, fontWeight: 500, py: 1.4 }}>
                            {desp.descricao}
                          </TableCell>
                          <TableCell sx={{ py: 1.4 }}>
                            <Box sx={{
                              display: 'inline-flex', bgcolor: '#F1F5F9', color: '#475569',
                              borderRadius: 1, px: 1.2, py: 0.3, fontSize: '0.75rem', fontWeight: 600,
                            }}>
                              {desp.categoria}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.4 }}>
                            <Typography sx={{ fontWeight: 700, color: RED, fontSize: '0.875rem' }}>
                              {fmt(desp.valor)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ── Tab 3: Documentos ─── */}
          {tab === 3 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: DARK }}>
                  Documentos Anexados
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadOpen(true)}
                  sx={{
                    background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                    boxShadow: 'none', textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                    '&:hover': { background: `linear-gradient(135deg, #EA580C 0%, ${ORANGE} 100%)`, boxShadow: 'none' },
                  }}
                >
                  Fazer Upload
                </Button>
              </Box>

              {documentos.length === 0 ? (
                <EmptyState icon={FolderIcon} message="Nenhum documento anexado" />
              ) : (
                <TableContainer>
                  <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F1F5F9' } }}>
                    <TableHead>
                      <TableRow>
                        <TH>Tipo</TH>
                        <TH>Nome do Arquivo</TH>
                        <TH>Data de Upload</TH>
                        <TH>Valor</TH>
                        <TH align="center">Ações</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentos.map((doc) => {
                        const tipoColors = {
                          nota_fiscal: { bg: `${BLUE}15`, color: BLUE },
                          recibo:      { bg: `${GREEN}15`, color: GREEN },
                        };
                        const tc = tipoColors[doc.tipo] || { bg: '#F1F5F9', color: '#64748B' };
                        return (
                          <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{
                                display: 'inline-flex', bgcolor: tc.bg, color: tc.color,
                                borderRadius: 1, px: 1.2, py: 0.3,
                                fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                              }}>
                                {doc.tipo.replace('_', ' ')}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', color: DARK, fontWeight: 500, py: 1.4 }}>
                              {doc.nomeArquivo}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', color: '#64748B', py: 1.4 }}>
                              {fmtDate(doc.createdAt)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', color: DARK, py: 1.4 }}>
                              {doc.valor ? fmt(doc.valor) : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.4 }}>
                              <Tooltip title="Download">
                                <IconButton size="small" onClick={() => handleDownload(doc.id)}
                                  sx={{ color: BLUE, '&:hover': { bgcolor: `${BLUE}12` } }}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton size="small" onClick={() => handleDelete(doc.id)}
                                  sx={{ color: RED, ml: 0.5, '&:hover': { bgcolor: `${RED}12` } }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Card>

      {/* ── Upload Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: DARK, pb: 1 }}>
          Fazer Upload de Documento
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Documento</InputLabel>
              <Select
                value={uploadData.tipo}
                label="Tipo de Documento"
                onChange={(e) => setUploadData({ ...uploadData, tipo: e.target.value })}
              >
                <MenuItem value="nota_fiscal">Nota Fiscal</MenuItem>
                <MenuItem value="recibo">Recibo</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Descrição" fullWidth value={uploadData.descricao}
              onChange={(e) => setUploadData({ ...uploadData, descricao: e.target.value })} />
            <TextField size="small" label="Data de Emissão" type="date" fullWidth
              InputLabelProps={{ shrink: true }} value={uploadData.dataEmissao}
              onChange={(e) => setUploadData({ ...uploadData, dataEmissao: e.target.value })} />
            <TextField size="small" label="Valor (opcional)" type="number" fullWidth
              value={uploadData.valor}
              onChange={(e) => setUploadData({ ...uploadData, valor: e.target.value })} />
            <TextField size="small" label="Observações" fullWidth multiline rows={3}
              value={uploadData.observacoes}
              onChange={(e) => setUploadData({ ...uploadData, observacoes: e.target.value })} />
            <Button variant="outlined" component="label" fullWidth
              sx={{ textTransform: 'none', borderColor: selectedFile ? ORANGE : '#CBD5E1',
                color: selectedFile ? ORANGE : '#64748B', borderRadius: 1.5 }}>
              {selectedFile ? selectedFile.name : 'Escolher Arquivo'}
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedFile(e.target.files[0])} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleUploadClose} sx={{ textTransform: 'none', color: '#64748B' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUploadSubmit}
            disabled={uploading || !selectedFile}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none',
              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
              '&:hover': { background: `linear-gradient(135deg, #EA580C 0%, ${ORANGE} 100%)`, boxShadow: 'none' },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {uploading ? 'Enviando...' : 'Enviar Documento'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PerfilUsuario;
